import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Container, Button, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Alert, TextField } from '@mui/material';

import WasteCharacterizationTable from "../../tables/wasteCharacterizationTable";

import { AppSetting } from "../../../types/appSetting";
import { ISelectOption } from "../../../types/list";
import { IWasteCharacterization } from "../../../types/wasteCharacterization";

import { AppSettingService } from '../../../services/appSettingService';
import { WasteCharacterizationService } from "../../../services/wasteCharacterizationService";
import { UtilityService } from "../../../services/utilityService";

import { AppConfig } from "../../../config/appConfig";
import { useUserContext } from "../../../contexts/userContext";

import Navigation from "../../header";
import PermissionGate from "../../permissionGate";
import Spinner from "../../spinner";

import "../../../styles/entity/wasteCharacterizationList.scss";


const WasteCharacterizationList = () => {
	const userContext = useUserContext();
	const [gridView, setGridView] = useState<IWasteCharacterization[]>();

	//Filtering
	const [appSettings, setAppSettings] = useState<AppSetting[]>([]);
	const [materialOptions, setMaterialOptions] = useState<ISelectOption[]>([]);
	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [statusOptions, setStatusOptions] = useState<string[]>(["Pending", "Active", "Expired", "Expiring Soon"]);
	const [profileFilter, setProfileFilter] = useState("");
	const [materialFilter, setMaterialFilter] = useState("All");
	const [customerFilter, setCustomerFilter] = useState("All");
	const [statusFilter, setStatusFilter] = useState("All");
	const [expireWarningDays, setExpireWarningDays] = useState<number>(30);

	const [isLoadingAppSettings, setIsLoadingAppSettings] = useState(true);
	const [isLoadingWasteCharacterizations, setIsLoadingWasteCharacterizations] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const loadWasteCharacterizationData = async () => {
		setIsLoadingWasteCharacterizations(true);
		setErrorMessage(undefined);

		WasteCharacterizationService.getWasteCharacterizations()
			.then((response) => {
				console.log('WasteCharacterizationService.getWasteCharacterizations: SUCCESS', response);
				let materials: ISelectOption[] = [];
				let customers: ISelectOption[] = [];

				let wasteCharacterizations = WasteCharacterizationService.mapWasteCharacterizations(response.data);

				wasteCharacterizations.map((item) => {
					if (item.materialId && !materials.some(m => m.value === item.materialId.toString())) {
						materials.push({  name: item.materialName, value: item.materialId.toString() });
					}

					if (item.customerId && !customers.some(c => c.value === item.customerId.toString())) {
						customers.push({ name: item.customerName, value: item.customerId.toString()});
					}
				});

				setGridView(wasteCharacterizations);
				setMaterialOptions(materials.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
				setCustomerOptions(customers.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
			})
			.catch((error) => {
				console.log('WasteCharacterizationService.getWasteCharacterizations: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}				
			})
			.finally(() => {
				setIsLoadingWasteCharacterizations(false);
			});
	}

	//WasteCharacterizationExpireWarningDays
	const getAppSettings = async () => {
		setIsLoadingAppSettings(true);
		AppSettingService.getAppSettings()
			.then((response) => {
				console.log('AppSettingService.getAppSettings: SUCCESS', response)
				setAppSettings(response.data);
			})
			.catch((error) => {
				console.log('AppSettingService.getAppSettings: ERROR', error)
			})
			.finally(() => {
				setIsLoadingAppSettings(false);
			});
	}

	useEffect(() => {
		getAppSettings();
		loadWasteCharacterizationData();
	}, [])

	useEffect(() => {
		if (appSettings?.length > 0) {
			const expireWarningSetting = appSettings.find(setting => setting.name == "WasteCharacterizationExpireWarningDays");
			if (expireWarningSetting) {
				setExpireWarningDays(Number(expireWarningSetting.value));
			}
		}
	}, [appSettings])

	const handleSelectCustomer = (event: SelectChangeEvent) => {
		setCustomerFilter(event.target.value);
	};

	const handleSelectMaterial = (event: SelectChangeEvent) => {
		setMaterialFilter(event.target.value);
	};

	const handleSelectStatus = (event: SelectChangeEvent) => {
		setStatusFilter(event.target.value);
	};

	return (
		<div className="hwi-waste-characterization-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageWasteCharacterization}>
					<section className="hwi-entity-search-bar">
						<div className="search-header">
							<div className="title">Waste Characterizations</div>
							<div><NavLink to="/wastecharacterization/add"><Button variant="outlined">New Waste Characterization</Button></NavLink></div>
						</div>
						<section className="search-options">
							<FormControl className="profile-filter" size="small">
								<TextField
									label="Profile #"
									name="profile"
									value={profileFilter}
									size="small"
									onChange={(event) => { setProfileFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Material</InputLabel>
								<Select
									value={materialFilter}
									label="Material"
									onChange={handleSelectMaterial}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{materialOptions.map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
							</FormControl>							
							<FormControl size="small">
								<InputLabel>Customer</InputLabel>
								<Select
									value={customerFilter}
									label="Customer"
									onChange={handleSelectCustomer}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{customerOptions.map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Status</InputLabel>
								<Select
									value={statusFilter}
									label="Status"
									onChange={handleSelectStatus}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{statusOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
						</section>
					</section>
					<section className="results">
						<Spinner dependency={isLoadingWasteCharacterizations || isLoadingAppSettings} text="Loading Waste Characterizations...">
							{errorMessage &&
								<Alert className="alert-error"
									severity="error">
									{errorMessage}
								</Alert>
							}
							{!errorMessage &&
								<WasteCharacterizationTable data={gridView} profile={profileFilter} material={materialFilter} customer={customerFilter} status={statusFilter} expireWarningDays={expireWarningDays}></WasteCharacterizationTable>
							}
						</Spinner>
					</section>
				</PermissionGate>
			</Container>
		</div>
	);
};

export default WasteCharacterizationList;